import {
  addMilliseconds,
  differenceInMilliseconds,
  formatISO,
  parseISO,
  set,
} from 'date-fns'
import { useMemo } from 'react'
import { useContentDragContext } from './ContentDragContext'
import { toDayKey } from './useIsContentOutsideMomentRange'

type ContentDropProposal = {
  newStart: Date
  newEnd: Date | null
}

type ContentDropForbiddenReason = 'before-moment' | null

export type ContentReschedulePayload = {
  contentId: string
  previousScheduledAt?: string
  scheduled_at: string
  takedown_scheduled_at: string | null
}

type UseContentDropProposalResult = {
  proposedRange: ContentDropProposal | null
  forbiddenReason: ContentDropForbiddenReason
  buildReschedulePayload: () => ContentReschedulePayload | null
}

/**
 * Shared drag-reschedule proposal for content drops. Preserves the
 * source post's clock-time and full in-market duration. The only
 * forbidden case is landing before the parent moment's `scheduled_at`;
 * post-moment-end drops are allowed (see `useIsContentOutsideMomentRange`).
 * Used by `ContentStack`, `SpanDropTarget`, and the Histogram days.
 *
 * `buildReschedulePayload()` returns the standard `onRescheduleContent`
 * payload for the current drop, or null when no valid drop is in flight.
 * `previousScheduledAt` is always included; consumers that don't need
 * it (e.g. WeekView grid drops) ignore the field.
 */
export const useContentDropProposal = (
  date: Date
): UseContentDropProposalResult => {
  const { draggedContent } = useContentDragContext()

  const proposedRange = useMemo<ContentDropProposal | null>(() => {
    if (!draggedContent) return null
    const priorStart = draggedContent.scheduledAt
      ? parseISO(draggedContent.scheduledAt)
      : date
    const newStart = set(date, {
      hours: priorStart.getHours(),
      minutes: priorStart.getMinutes(),
      seconds: priorStart.getSeconds(),
      milliseconds: priorStart.getMilliseconds(),
    })
    const delta = differenceInMilliseconds(newStart, priorStart)
    const newEnd = draggedContent.takedownAt
      ? addMilliseconds(parseISO(draggedContent.takedownAt), delta)
      : null
    return { newStart, newEnd }
  }, [draggedContent, date])

  const forbiddenReason = useMemo<ContentDropForbiddenReason>(() => {
    if (!draggedContent || !proposedRange) return null
    // When the parent moment isn't hydrated in this view its start is
    // unknown (content carries only the moment id, not its schedule), so the
    // before-moment guard can't run. We allow the drop rather than block it:
    // an unhydrated moment is virtually always one scheduled in an earlier
    // week, so a within-week drop stays after its start.
    if (!draggedContent.momentScheduledAt) return null
    // Key both sides through `toDayKey` so they sit in the same column frame
    // the cards render in; comparing the raw ISO day against the rendered
    // column would drift by a day at the timezone boundary.
    const newStartDayKey = toDayKey(proposedRange.newStart)
    const momentStartDayKey = toDayKey(draggedContent.momentScheduledAt)
    if (!newStartDayKey || !momentStartDayKey) return null
    return newStartDayKey < momentStartDayKey ? 'before-moment' : null
  }, [draggedContent, proposedRange])

  const buildReschedulePayload = (): ContentReschedulePayload | null => {
    if (!draggedContent || forbiddenReason || !proposedRange) return null
    return {
      contentId: draggedContent.id,
      previousScheduledAt: draggedContent.scheduledAt ?? undefined,
      scheduled_at: formatISO(proposedRange.newStart),
      takedown_scheduled_at: proposedRange.newEnd
        ? formatISO(proposedRange.newEnd)
        : null,
    }
  }

  return { proposedRange, forbiddenReason, buildReschedulePayload }
}
